import { useState, useMemo } from "react";
import { RiArrowDropDownLine } from "react-icons/ri";
import { postContact } from "../api/contact"; // ← adjust path if needed
import { executeCaptcha } from "../utils/recaptcha";

const TOPICS = [
  "General",
  "Workshops",
  "1:1 Sessions",
  "Collaboration",
  "Press / Media",
  "Other",
];

const INITIAL_FORM = {
  name: "",
  email: "",
  topic: "General",
  message: "",
  subscribe: false,
  // honeypot field (should stay blank)
  company: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email)) next.email = "Please enter a valid email.";
    if (!form.message.trim()) next.message = "Please enter a message.";
    if (form.company.trim()) next.company = "Spam detected."; // honeypot
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const sending = status === "sending";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!validate()) return;

    try {
      setStatus("sending");
      const captchaToken = await executeCaptcha("contact_form");

      // Send to your server
      await postContact({
        name: form.name,
        email: form.email,
        topic: form.topic,
        message: form.message,
        subscribe: !!form.subscribe,
        company: form.company, // honeypot stays blank for humans
        captchaToken,
      });

      setStatus("success");
      setForm(INITIAL_FORM);
      setErrors({});
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  // Helpful ARIA for status text
  const statusText = useMemo(() => {
    if (status === "success") return "Thanks! I’ll get back to you soon.";
    if (status === "error") return "Something went wrong. Please try again.";
    return "";
  }, [status]);

  return (
    <main className="page contact-page" aria-labelledby="contact-heading">
      <header className="page-header contact-header">
        <h1 id="contact-heading" className="page-title">Contact</h1>
        <p className="page-subtitle contact-subtitle">
          Feel free to reach out to us using the form below or by email! We will get back to you as soon as possible.
        </p>
      </header>

      <section className="container content-grid content-grid--aside contact-body">
        <form className="contact-form card" onSubmit={onSubmit} noValidate aria-busy={sending}>
          {/* Honeypot (hidden from users) */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="company">Company</label>
            <input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              value={form.company}
              onChange={onChange}
              tabIndex={-1}
            />
          </div>

          <div className="form-grid">
            {/* Name */}
            <div className={`form-field ${errors.name ? "has-error" : ""}`}>
              <label htmlFor="name">Name</label>
              <input
                className={`field ${errors.name ? "field--error" : ""}`}
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={onChange}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-err" : undefined}
                required
              />
              {errors.name && (
                <div className="error" id="name-err" role="alert">
                  {errors.name}
                </div>
              )}
            </div>

            {/* Email */}
            <div className={`form-field ${errors.email ? "has-error" : ""}`}>
              <label htmlFor="email">Email</label>
              <input
                className={`field ${errors.email ? "field--error" : ""}`}
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={onChange}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-err" : undefined}
                required
              />
              {errors.email && (
                <div className="error" id="email-err" role="alert">
                  {errors.email}
                </div>
              )}
            </div>

            {/* Topic */}
            <div className="form-field full">
              <label htmlFor="topic">Topic</label>
              <div className="select-wrap">
                <select
                  className="field"
                  id="topic"
                  name="topic"
                  value={form.topic}
                  onChange={onChange}
                >
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                {/* Inline Chevron Icon */}
                <RiArrowDropDownLine className="select-chevron" />
              </div>
            </div>

            {/* Message */}
            <div className={`form-field full ${errors.message ? "has-error" : ""}`}>
              <label htmlFor="message">Message</label>
              <textarea
                className={`field ${errors.message ? "field--error" : ""}`}
                id="message"
                name="message"
                rows={6}
                value={form.message}
                onChange={onChange}
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-err" : undefined}
                required
              />
              {errors.message && (
                <div className="error" id="message-err" role="alert">
                  {errors.message}
                </div>
              )}
            </div>

            {/* Subscribe */}
            <label className="checkbox">
              <input
                type="checkbox"
                name="subscribe"
                checked={form.subscribe}
                onChange={onChange}
              />
              <span>Send me occasional updates</span>
            </label>
          </div>

          {/* Submit / Success button */}
          {status === "success" ? (
            <button className="contact-submit btn btn--success" type="button" disabled>
              ✓ Sent
            </button>
          ) : (
            <button className="contact-submit btn btn--green" type="submit" disabled={sending}>
              {sending ? "Sending…" : "Send Message"}
            </button>
          )}

          {/* Status */}
          <div className="status" aria-live="polite">
            {statusText && (
              <span className={status === "success" ? "ok" : "err"}>{statusText}</span>
            )}
          </div>
        </form>

        <aside className="contact-aside" aria-label="Other ways to reach us">
          <div className="card">
            <h3>Reach us through:</h3>
            <ul>
              <li>
                Email:{" "}
                <a href="mailto:livingroominquiries@proton.me">
                  livingroominquiries@proton.me
                </a>
              </li>
              <li>
                Instagram:{" "}
                <a href="https://instagram.com/" target="_blank" rel="noreferrer">
                  @livingroominquiries
                </a>
              </li>
              <li>
                YouTube:{" "}
                <a
                  href="https://www.youtube.com/@LivingRoomInquiries"
                  target="_blank"
                  rel="noreferrer"
                >
                  Living Room Inquiries
                </a>
              </li>
            </ul>
          </div>

          <div className="card note">
            <h3>Privacy</h3>
            <p>
              Your message is used solely to respond and won’t be shared. If you opt in,
              you’ll receive occasional updates—you can unsubscribe anytime.
            </p>
          </div>
        </aside>
      </section>
    </main>
  );
}
